// packages imports
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import { FC, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
// component import
import { ExportCsv } from './ExportCsv'
import { SortCasesByUser } from './SortCasesByUser'
// others
import { CrossIcon } from '../../assets/images'
import {
  CASE_SEARCH_INFO_TEXT,
  MIN_SEARCH_LENGTH,
  MIN_SEARCH_LENGTH_TEXT,
  QUERY_PARAMS,
  SEARCH_TEXT,
  SORT_DIR_TYPE
} from '../../constants'
import { AppContext, AuthContext, CaseContext } from '../../context'
import { ActionType } from '../../context/AppContextReducer'
import { SearchFieldProps } from '../../interfaceTypes'
import { anchorOrigin, transformOrigin } from '../../theme/styleConstants'
import { CaseActionType } from './caseReducer'
import {
  BillingType,
  CaseStatus,
  CasesInput,
  ExternalUser,
  FetchAllCasesQuery,
  Maybe,
  Office,
  OrderCaseBy
} from '../../../generated'
import { ApolloQueryResult, LazyQueryExecFunction } from '@apollo/client'
import { Exact } from '../../../generated'
import ClientDropdown from '../common/ClientDropdown'
import OfficeDropdown from '../common/OfficeDropdown'
import { removeUnderScoreOrHyphenAndToLowerCase, titleize } from '../../utils'

type CasesFilterProps = SearchFieldProps & {
  showSearch?: boolean
  showSortBy?: boolean
  showLawType?: boolean
  showSortDir?: boolean
  showClient?: boolean
  showOffice?: boolean
  showSortByUser?: boolean
  showExport?: boolean
  showReset?: boolean
  showBillingType?: boolean
  loading?: boolean
  refetch?: () => Promise<ApolloQueryResult<FetchAllCasesQuery>>
  fetchAllCases?: LazyQueryExecFunction<
    FetchAllCasesQuery,
    Exact<{
      casesInput: CasesInput
    }>
  >
}

export const CasesFilter: FC<CasesFilterProps> = ({
  casesLength,
  sortByUser,
  showSearch = true,
  showSortBy = true,
  showLawType = true,
  showSortDir = true,
  showClient = true,
  showOffice = true,
  showSortByUser = true,
  showExport = true,
  showBillingType = true,
  loading = false,
  refetch,
  fetchAllCases
}) => {
  const { isAdmin, isSuperAdmin } = useContext(AuthContext)
  const { dispatch: reducerDispatch, currentPage } = useContext(AppContext)
  const {
    allLawTypes,
    dispatch,
    searchValue,
    lawTypeId,
    orderBy,
    orderByDirection,
    clientId,
    officeName,
    caseFilter,
    isWorkCompleted,
    billingType
  } = useContext(CaseContext)

  const [inputValue, setInputValue] = useState(searchValue)
  const [submittedValue, setSubmittedValue] = useState(searchValue)

  // Sync local input when context searchValue is cleared externally (tab switch, reset, etc.)
  useEffect(() => {
    if (searchValue === '') {
      setInputValue('')
      setSubmittedValue('')
    }
  }, [searchValue])
  const { pathname } = useLocation()
  const isConsultPath = pathname.includes('consult')
  const [searchParams, setSearchParams] = useSearchParams()
  const useRefForSortByUser = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const clearIndicator = useRefForSortByUser?.current?.getElementsByClassName(
    'MuiAutocomplete-clearIndicator'
  )[0] as HTMLDivElement
  const mediumViewport = useMediaQuery('(max-width:1138px)')

  const commonFilterSteps = () => {
    dispatch({ type: CaseActionType.SET_SEARCH_VALUE, searchValue: '' })
    reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    searchParams.set('currentPage', currentPage.toString())
  }

  const handleChange = (event: SelectChangeEvent) => {
    const { value } = event.target
    dispatch({ type: CaseActionType.SET_LAW_TYPE_ID, lawTypeId: value === QUERY_PARAMS.ALL ? '' : value })
    commonFilterSteps()
    searchParams.set('lawType', value === QUERY_PARAMS.ALL ? '' : value)
    setSearchParams(searchParams)
  }

  const handleSortOrderChange = (event: SelectChangeEvent) => {
    const { value } = event.target
    dispatch({ type: CaseActionType.SET_CASE_ORDER_BY, orderBy: value as OrderCaseBy })
    commonFilterSteps()
    searchParams.set('orderBy', value)
    setSearchParams(searchParams)
  }

  const handleSortDirectionChange = (event: SelectChangeEvent) => {
    const { value } = event.target
    dispatch({ type: CaseActionType.SET_CASE_ORDER_DIRECTION, orderByDirection: value as SORT_DIR_TYPE })
    commonFilterSteps()
    searchParams.set(QUERY_PARAMS.ORDER_DIRECTION, value)
    setSearchParams(searchParams)
  }

  const handleBillingTypeChange = (event: SelectChangeEvent) => {
    const { value } = event.target
    dispatch({ type: CaseActionType.SET_BILLING_TYPE, billingType: value })
    commonFilterSteps()
    searchParams.set(QUERY_PARAMS.BILLING_TYPE, value)
    setSearchParams(searchParams)
  }

  const handleClientChange = (value: Maybe<ExternalUser>) => {
    dispatch({ type: CaseActionType.SET_CLIENT_ID, clientId: value?.id || '' })
    commonFilterSteps()
    searchParams.set(QUERY_PARAMS.CLIENT, value?.id || '')
    setSearchParams(searchParams)
  }

  const handleOfficeChange = (value: Maybe<Office>) => {
    dispatch({ type: CaseActionType.SET_OFFICE_NAME, officeName: value?.name || '' })
    commonFilterSteps()
    searchParams.set(QUERY_PARAMS.OFFICE, value?.name || '')
    setSearchParams(searchParams)
  }

  const isTooShort = inputValue.length > 0 && inputValue.length < MIN_SEARCH_LENGTH

  const handleSearch = () => {
    if (loading || inputValue === submittedValue || isTooShort) return
    setSubmittedValue(inputValue)
    dispatch({ type: CaseActionType.SET_SEARCH_VALUE, searchValue: inputValue })
    reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    searchParams.set('searchValue', inputValue)
    searchParams.set('currentPage', '1')
    setSearchParams(searchParams)
  }

  const clearSearchHandler = () => {
    setInputValue('')
    setSubmittedValue('')
    dispatch({ type: CaseActionType.SET_SEARCH_VALUE, searchValue: '' })
    searchParams.set('searchValue', '')
    setSearchParams(searchParams)
  }

  const resetFilters = () => {
    setInputValue('')
    setSubmittedValue('')
    dispatch({ type: CaseActionType.SET_SEARCH_VALUE, searchValue: '' })
    dispatch({ type: CaseActionType.SET_LAW_TYPE_ID, lawTypeId: '' })
    reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    dispatch({ type: CaseActionType.SET_SORT_CASES_BY_USER, sortCasesByUser: undefined })
    dispatch({ type: CaseActionType.SET_CASE_ORDER_BY, orderBy: OrderCaseBy.MatterNo })

    if (clearIndicator) {
      clearIndicator.click()
      if (inputRef.current) {
        inputRef?.current?.blur()
      }
    }
    searchParams.delete('lawType')
    searchParams.delete('currentPage')
    searchParams.delete('searchValue')
    searchParams.delete('userId')
    searchParams.delete('userLabel')
    searchParams.delete('userRole')
    searchParams.delete('orderBy')
    setSearchParams(searchParams)

    // Force a network fetch when resetting filters to bypass cache
    if (fetchAllCases) {
      // Construct query variables with reset values
      const resetQueryVariables = {
        casesInput: {
          paginationOptions: {
            page: 1,
            limit: 10
          },
          orderBy: OrderCaseBy.MatterNo,
          orderByDirection: orderByDirection || 'DESC',
          isConsultCase: isConsultPath,
          status: caseFilter === 'close' ? CaseStatus.Completed : CaseStatus.Active,
          searchValue: '',
          lawTypeId: '',
          clientId: clientId || undefined,
          officeName: officeName || undefined,
          billingType: billingType === QUERY_PARAMS.ALL ? undefined : (billingType as BillingType),
          isWorkCompleted: caseFilter === 'close' ? undefined : isWorkCompleted
        }
      }

      fetchAllCases({
        variables: resetQueryVariables,
        fetchPolicy: 'network-only'
      }).catch(() => {
        // Silently handle errors - the query will retry on next state change
      })
    }
  }

  return (
    <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap="10px" alignItems="baseline">
      {showSearch && (
        <Box maxWidth={380} width="100%">
          <Box display="flex" gap="4px" alignItems="flex-start">
            <TextField
              placeholder={SEARCH_TEXT}
              fullWidth
              size="small"
              value={inputValue}
              error={isTooShort}
              helperText={isTooShort ? MIN_SEARCH_LENGTH_TEXT : ''}
              FormHelperTextProps={{ sx: { ml: 0 } }}
              onChange={event => {
                setInputValue(event.target.value.slice(0, 50))
              }}
              inputProps={{ maxLength: 50 }}
              onKeyDown={event => {
                if (event.key === 'Enter') handleSearch()
              }}
              InputProps={{
                endAdornment: inputValue ? (
                  <InputAdornment position="end" onClick={clearSearchHandler}>
                    <IconButton sx={{ p: 0, svg: { maxWidth: '10px' } }} onClick={clearSearchHandler}>
                      <CrossIcon />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }}
            />
            <Button
              color="primary"
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={loading || inputValue === submittedValue || isTooShort}
            >
              Search
            </Button>
          </Box>
          <Typography variant="caption" color="wardGray" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
            {CASE_SEARCH_INFO_TEXT}
          </Typography>
        </Box>
      )}

      <Box display="flex" gap={mediumViewport ? '20px' : '10px'} alignItems="flex-end" flexWrap="wrap">
        {showBillingType && (
          <Box mt={4}>
            <FormControl>
              <InputLabel id="billingType">Billing Type:</InputLabel>
              <Select
                label={'Billing Type:'}
                id="billingType"
                size="small"
                onChange={handleBillingTypeChange}
                displayEmpty={true}
                value={searchParams.get(QUERY_PARAMS.BILLING_TYPE) ?? QUERY_PARAMS.ALL}
                sx={{ width: 250 }}
                MenuProps={{
                  anchorOrigin,
                  transformOrigin,
                  style: {
                    maxHeight: 400
                  }
                }}
              >
                <MenuItem value={QUERY_PARAMS.ALL}>All</MenuItem>
                {Object.values(BillingType)
                  .filter(type => type !== BillingType.Consult)
                  .map(type => (
                    <MenuItem value={type}>{titleize(removeUnderScoreOrHyphenAndToLowerCase(type))}</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {showClient && (
          <Box>
            <ClientDropdown
              onChange={handleClientChange}
              label="Client"
              selectedClientId={clientId}
              size="small"
              sx={{ width: '200px' }}
            />
          </Box>
        )}

        {showLawType && (
          <Box mt={5}>
            <FormControl>
              <InputLabel id="sortByLawType">Law type:</InputLabel>
              <Select
                labelId="sortByLawType"
                id="sortByLawType"
                label={'Law Type:'}
                onChange={handleChange}
                displayEmpty={true}
                value={searchParams.get('lawType') || lawTypeId || QUERY_PARAMS.ALL}
                size="small"
                sx={{ width: 250 }}
                MenuProps={{
                  anchorOrigin: anchorOrigin,
                  transformOrigin: transformOrigin,
                  style: {
                    maxHeight: 400
                  }
                }}
              >
                <MenuItem value={QUERY_PARAMS.ALL}>All</MenuItem>
                {allLawTypes?.map(item => {
                  const { id, name } = item || {}

                  return (
                    <MenuItem sx={{ width: mediumViewport ? 252 : 200 }} value={id} key={id}>
                      {name}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </Box>
        )}

        {showSortBy && (
          <Box display="flex" gap={mediumViewport ? '20px' : '10px'} alignItems="flex-end" flexWrap="wrap">
            <Box>
              <FormControl>
                <InputLabel id="sortByLawType">Sort By:</InputLabel>
                <Select
                  labelId="sortBy"
                  id="sortBy"
                  label={'Sort By:'}
                  onChange={handleSortOrderChange}
                  displayEmpty={true}
                  value={searchParams.get('orderBy') || orderBy}
                  size="small"
                  sx={{ width: mediumViewport ? 262 : 130 }}
                  MenuProps={{
                    anchorOrigin: anchorOrigin,
                    transformOrigin: transformOrigin,
                    style: {
                      maxHeight: 400
                    }
                  }}
                >
                  <MenuItem value={OrderCaseBy.Title}>Case Title</MenuItem>
                  <MenuItem value={OrderCaseBy.MatterNo}>Matter #</MenuItem>
                  <MenuItem value={OrderCaseBy.Recent}>Recent</MenuItem>
                  <MenuItem value={OrderCaseBy.Court}>Court</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}

        {showSortDir && (
          <Box>
            <FormControl>
              <InputLabel id="sortByDir">Sort Dir:</InputLabel>
              <Select
                labelId="A/D"
                label={'Sort Dir:'}
                id="sortByDir"
                size="small"
                onChange={handleSortDirectionChange}
                displayEmpty={true}
                value={searchParams.get(QUERY_PARAMS.ORDER_DIRECTION) ?? 'DESC'}
                sx={{ width: mediumViewport ? 262 : 130 }}
                MenuProps={{
                  anchorOrigin,
                  transformOrigin,
                  style: {
                    maxHeight: 400
                  }
                }}
              >
                <MenuItem value={'ASC'}>Asc</MenuItem>
                <MenuItem value={'DESC'}>Desc</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {(isAdmin || isSuperAdmin) && showOffice && (
          <Box>
            <OfficeDropdown
              selectedOffice={officeName}
              sx={{ minWidth: 250 }}
              onChange={handleOfficeChange}
              size={'small'}
              label="Profit Center/Location"
            />
          </Box>
        )}

        {sortByUser && showSortByUser && (
          <Box width={250}>
            <SortCasesByUser useRefForSortByUser={useRefForSortByUser} inputRef={inputRef} />
          </Box>
        )}

        <Box
          display="flex"
          gap={mediumViewport ? '20px' : '10px'}
          maxWidth={350}
          flexDirection={mediumViewport ? 'column' : 'row'}
        >
          {isAdmin && !showExport && <ExportCsv casesLength={casesLength!} />}
        </Box>

        <Tooltip title="Reset all filters" placement="top">
          {mediumViewport ? (
            <Button color="primary" variant="outlined" size="small" onClick={resetFilters} aria-label="Reset filter">
              Reset
            </Button>
          ) : (
            <IconButton color="primary" onClick={resetFilters}>
              <FilterAltOffIcon />
            </IconButton>
          )}
        </Tooltip>
      </Box>
    </Box>
  )
}
